import { ChangeEvent, Dispatch, SetStateAction, memo } from "react";

import { Link } from "react-router-dom";
import { Box, Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { IIMage } from "@modules/images";
import { getImageUrl } from "@app/helpers/getUploadUrl";
import { allowFiles } from "@app/helpers/uploadFileValidation";
import ImageDialog from "@app/components/ImageDialog";
import AreYouSureDelete from "@app/components/AreYouSureDelete";
import { RenderNewUploadingImagesList } from "@app/components/CreateEditCommon";
import { Image, ImageWrapper } from "@app/components/RenderImagesByBase64/styled";
import { FieldErrors } from "react-hook-form";

import { CreateCertificateState, ICreateCertificateFormData } from "./utility/types";
import { FilesWrapperStyle, StyledFormHelperText, VisuallyHiddenInput } from "./styled";
import { LocalState } from "./utility/useMutationCertificatesLogic";

interface IFilesWrapper {
  pdf: FileList;
  previews: string[];
  pdfFile?: IIMage;
  otherFile?: IIMage;
  imageSrcForAction: string;
  isImageViewOpen: boolean;
  isDeleteModalOpen: boolean;
  errors: FieldErrors<ICreateCertificateFormData>;
  setState: Dispatch<SetStateAction<LocalState>>;
  onChangeUpload: (event: ChangeEvent<HTMLInputElement>, isPdf?: boolean) => void;
}

const FilesWrapper = ({
  pdf,
  errors,
  pdfFile,
  previews,
  otherFile,
  isImageViewOpen,
  isDeleteModalOpen,
  imageSrcForAction,
  setState,
  onChangeUpload,
}: IFilesWrapper) => {
  const handleCloseImageDialog = () =>
    setState((prev) => ({ ...prev, isImageViewOpen: false }));

  const handleCloseDelete = () =>
    setState((prev) => ({ ...prev, isDeleteModalOpen: false }));

  const handleDelete = () =>
    setState((prev) => ({ ...prev, imageSrcForAction }));

  const UploadButton = ({
    label,
    name,
    accept,
    onChange,
  }: {
    label: string;
    name: string;
    accept: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  }) => {
    return (
      <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}>
        <p>{label}</p>
        <VisuallyHiddenInput
          type="file"
          name={name}
          accept={accept}
          onChange={onChange}
        />
      </Button>
    )
  };

  const renderFilePreview = () => {
    if (previews.length) {
      return null;
    }

    if (!otherFile) {
      return null;
    }

    // comes from resusable jsx components
    return (
      <ImageWrapper>
        <Image
          width={150}
          height={150}
          src={getImageUrl(otherFile.url)}
          alt="product"
        />
      </ImageWrapper>
    );
  };

  const renderPdfSection = () => {
    if (pdf?.length) {
      return <Link target="_blank" to="#">{`New Uploaded File - ${pdf[0].name}`}</Link>;
    }

    if (pdfFile) {
      return <Link target="_blank" to={getImageUrl(pdfFile.url)}>View PDF</Link>;
    }

    return null;
  };

  return (
    <FilesWrapperStyle>
      <div>
        <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-start" }}>
          {renderFilePreview()}
        </Box>

        <RenderNewUploadingImagesList<CreateCertificateState>
          newPreviewsList={previews}
          togetherImagesLength={previews.length}
          setState={setState}
        />

        <ImageDialog
          url={imageSrcForAction}
          isOpen={isImageViewOpen}
          onClose={handleCloseImageDialog}
        />

        <AreYouSureDelete
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onDelete={handleDelete}
        />

        {errors.images?.message && <StyledFormHelperText id="messages">{errors.images?.message}</StyledFormHelperText>}
        <UploadButton
          label="Upload Pictures"
          name="images"
          accept={allowFiles.join(", ")}
          onChange={onChangeUpload}
        />
      </div>

      <div>
        {renderPdfSection()}
        <br />
        {errors.pdf?.message && <StyledFormHelperText id="messages">{errors.pdf?.message}</StyledFormHelperText>}
        <UploadButton
          label="Upload PDF"
          name="pdf"
          accept="application/pdf"
          onChange={(e) => onChangeUpload(e, true)}
        />
      </div>
    </FilesWrapperStyle>
  );
};

export default memo(FilesWrapper);
